// src/platform/firebase/clientFactory.ts
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { getDatabase, connectDatabaseEmulator, type Database } from 'firebase/database'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { resolveFirebaseRuntime } from './runtime'

// Cache per stage to avoid double inits
const singletons: Record<
  string,
  {
    app: FirebaseApp
    db: Firestore
    auth: Auth
    functions: Functions
    storage: FirebaseStorage
    realtimeDb: Database
  }
> = {}

export async function createClientFirebase() {
  const cfg = resolveFirebaseRuntime('client')
  const key = `${cfg.stage}`

  if (singletons[key]) return singletons[key]

  if (!cfg.webConfig) {
    throw new Error('Missing web Firebase config')
  }

  // Use the existing Firebase app and all services from the main app
  // This avoids re-initialization errors with emulators
  const { app, db, auth, functions, storage, realtimeDb } = await import('@/firebase/config')

  // Emulators are already connected by the main app
  // No need to connect them again here

  // App Check is already initialized by the main app
  // No need to initialize it again here

  const out = { app, db, auth, functions, storage, realtimeDb }
  singletons[key] = out
  return out
}

/** Helper to set Identity Platform tenant before any sign-in */
export function setClientAuthTenant(auth: Auth, tenantId: string) {
  ;(auth as any).tenantId = tenantId
}
